import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserOrgMembershipsCard } from '@/core/UserOrgMembershipsCard';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Switch,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ViewToolbar } from '@/components/ViewToolbar';
import { saveShortcutLabel } from '@/hooks/useSaveShortcut';
import { JsonFormsThemeProvider } from '@/components/forms/JsonFormsTheme';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { getErrorMessage } from '@/lib/apiClient';
import { checkOfflineAndNotify } from '@/lib/offlineGuard';
import {
  useUser,
  useCreateUser,
  useUpdateUser,
  useUserRoles,
  useAssignableRoles,
} from '@/hooks/identity/useIdentity';
import {
  CreateIdentityUser,
  UpdateIdentityUser,
} from '@/types/identity/Identity';
import { UserAccountTypeChip } from '@/core/UserAccountTypeChip';
import { resolveUserAccountType, AUTHENTICATION_CLASS_PROPERTY } from '@/core/userAccountType';

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const { data: user, isLoading } = useUser(id!, { enabled: !isNew });
  const { data: userRoleNames } = useUserRoles(id!);
  const { data: assignableRoles } = useAssignableRoles();
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();

  const { t } = useTranslation();
  const [apiErrors, setApiErrors] = useState<ValidationError[]>([]);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);

  // Form state
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [lockoutEnabled, setLockoutEnabled] = useState(true);
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());

  // Populate from server data
  useEffect(() => {
    if (user) {
      setUserName(user.userName);
      setEmail(user.email);
      setName(user.name || '');
      setSurname(user.surname || '');
      setPhoneNumber(user.phoneNumber || '');
      setIsActive(user.isActive);
      setLockoutEnabled(user.lockoutEnabled);
    }
  }, [user]);

  useEffect(() => {
    if (userRoleNames) {
      setSelectedRoles(new Set(userRoleNames));
    }
  }, [userRoleNames]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // D15: for an Entra-authenticated user the directory owns the name, and the
  // server rejects any save that CHANGES it. Presenting the fields read-only is
  // the same rule stated before the admin types, rather than after — the save
  // still carries the unchanged values, which is what the server permits.
  //
  // Reads the authentication class directly rather than going through
  // resolveUserAccountType: a support identity is Entra-class too, so its name
  // is equally directory-mastered, but the chip reports it as Support.
  const isExternallyManagedProfile =
    user?.extraProperties?.[AUTHENTICATION_CLASS_PROPERTY] === 'Entra';

  const handleSave = () => {
    if (checkOfflineAndNotify(setApiErrors, setValidationPanelOpen, t)) return;
    setApiErrors([]);
    const roleNames = Array.from(selectedRoles);

    if (isNew) {
      const data: CreateIdentityUser = {
        userName,
        email,
        name: name || undefined,
        surname: surname || undefined,
        phoneNumber: phoneNumber || undefined,
        password,
        isActive,
        lockoutEnabled,
        roleNames,
      };
      createMutation.mutate(data, {
        onSuccess: (created) => {
          navigate(`/core/users/${created.id}`, { replace: true });
        },
        onError: (error: unknown) => {
          const errors = getValidationErrors(error);
          if (errors.length > 0) {
            setApiErrors(errors);
            setValidationPanelOpen(true);
          } else {
            setApiErrors([{ field: 'form', message: getErrorMessage(error), instancePath: '#', members: [] }]);
            setValidationPanelOpen(true);
          }
        },
      });
    } else {
      const data: UpdateIdentityUser = {
        userName,
        email,
        name: name || undefined,
        surname: surname || undefined,
        phoneNumber: phoneNumber || undefined,
        password: password || undefined,
        isActive,
        lockoutEnabled,
        concurrencyStamp: user?.concurrencyStamp,
        roleNames,
      };
      updateMutation.mutate(
        { id: id!, data },
        {
          onSuccess: () => { setApiErrors([]); },
          onError: (error: unknown) => {
            const errors = getValidationErrors(error);
            if (errors.length > 0) {
              setApiErrors(errors);
              setValidationPanelOpen(true);
            } else {
              setApiErrors([{ field: 'form', message: getErrorMessage(error), instancePath: '#', members: [] }]);
              setValidationPanelOpen(true);
            }
          },
        },
      );
    }
  };

  const toggleRole = (roleName: string) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(roleName)) next.delete(roleName);
      else next.add(roleName);
      return next;
    });
  };

  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const saveDisabled = isSaving || !userName || !email || (isNew && !password);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ValidationErrorBanner
        errors={apiErrors}
        onExpand={() => setValidationPanelOpen(true)}
        onClear={() => { setApiErrors([]); setValidationPanelOpen(false); }}
      />

      <ViewToolbar showBackButton onBack={() => navigate('/core/users')} onSave={handleSave} saveDisabled={saveDisabled}>
        <Tooltip title={saveShortcutLabel()}>
          <span>
            <Button
              variant="outlined"
              onClick={handleSave}
              disabled={saveDisabled}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                ...(saveDisabled
                  ? { borderColor: 'grey.300', color: 'grey.400' }
                  : { borderColor: 'primary.main', color: 'primary.main' }),
              }}
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </span>
        </Tooltip>
      </ViewToolbar>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" gutterBottom>
            {isNew ? t('identity.createUser') : t('identity.editUser', { name: user?.userName ?? '' })}
          </Typography>

          <JsonFormsThemeProvider density="compact" variant="standard">
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2">{t('identity.account')}</Typography>
                {/*
                  Only for an existing user: a new one has no account type until
                  it is saved, and the create form only ever produces a local
                  account — the class is set by the external-login layer, never
                  here.
                */}
                {!isNew && user && (
                  <UserAccountTypeChip accountType={resolveUserAccountType(user.extraProperties)} />
                )}
              </Box>

              {isExternallyManagedProfile && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('Nexo:Sso:ExternallyManagedProfileFieldImmutable')}
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {/*
                  autoComplete is load-bearing here, not cosmetic. This is an
                  administrator editing SOMEONE ELSE's account, but the browser
                  sees a text input followed by a password input and treats the
                  pair as a sign-in form — Chrome filled the admin's own saved
                  credentials into Phone Number and New Password, and Save
                  submits both (the phone number is overwritten and the edited
                  user's password is reset to the admin's). "new-password" on
                  the password field is what actually breaks that heuristic;
                  "off" alone is widely ignored on text inputs.
                */}
                <TextField
                  label={t('identity.username')}
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                  size="small"
                  autoComplete="off"
                />
                <TextField
                  label={t('identity.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  size="small"
                  autoComplete="off"
                />
                <TextField
                  label={t('identity.firstName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  size="small"
                  autoComplete="off"
                  slotProps={{ input: { readOnly: isExternallyManagedProfile } }}
                  helperText={isExternallyManagedProfile ? 'Managed in Microsoft 365' : undefined}
                />
                <TextField
                  label={t('identity.lastName')}
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  size="small"
                  autoComplete="off"
                  slotProps={{ input: { readOnly: isExternallyManagedProfile } }}
                  helperText={isExternallyManagedProfile ? 'Managed in Microsoft 365' : undefined}
                />
                <TextField
                  label={t('identity.phoneNumber')}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  size="small"
                  autoComplete="off"
                />
                <TextField
                  label={isNew ? t('identity.password') : t('identity.newPassword')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required={isNew}
                  size="small"
                  helperText={isNew ? undefined : t('identity.passwordKeepHint')}
                  autoComplete="new-password"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                <FormControlLabel
                  control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                  label={t('common.active')}
                />
                <FormControlLabel
                  control={<Switch checked={lockoutEnabled} onChange={(e) => setLockoutEnabled(e.target.checked)} />}
                  label={t('identity.lockoutEnabled')}
                />
              </Box>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{t('identity.roles')}</Typography>
              {!assignableRoles ? (
                <CircularProgress size={20} />
              ) : assignableRoles.length === 0 ? (
                <Alert severity="info">{t('identity.noRoles')}</Alert>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.5 }}>
                  {assignableRoles.map((role) => (
                    <FormControlLabel
                      key={role.id}
                      control={
                        <Checkbox
                          checked={selectedRoles.has(role.name)}
                          onChange={() => toggleRole(role.name)}
                          size="small"
                        />
                      }
                      label={role.name}
                    />
                  ))}
                </Box>
              )}
            </Paper>

            {!isNew && id && (
              <UserOrgMembershipsCard
                userId={id}
                userName={[name, surname].filter(Boolean).join(' ') || userName}
              />
            )}
          </JsonFormsThemeProvider>
        </Box>

        <ValidationErrorPanel
          errors={apiErrors}
          open={validationPanelOpen}
          onClose={() => setValidationPanelOpen(false)}
        />
      </Box>
    </Box>
  );
}
